import { useEffect, useMemo, useRef, useState } from "react";
import { api, streamChat } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  PaperPlaneTiltIcon,
  PlusIcon,
  TrashIcon,
  WrenchIcon,
  ChatCircleIcon,
  CaretRightIcon,
  WarningIcon,
  CheckCircleIcon,
  ImageIcon,
  XIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
} from "@phosphor-icons/react";

const AVATAR =
  "https://static.prod-images.emergentagent.com/jobs/b31b23f3-02f4-45a2-876e-3a1e8855c361/images/e74b9230c32bef1bf697a7c25daacf86ecc3920874f9df82e1c1fd10dd5031e0.png";
const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function MessageItem({ m }) {
  if (m.role === "tool") {
    return (
      <div
        className="my-3 border border-zinc-800 rounded-sm bg-black overflow-hidden"
        data-testid="message-tool"
      >
        <div className="px-3 py-2 flex items-center gap-2 border-b border-zinc-800 bg-zinc-950">
          <WrenchIcon size={14} className="text-emerald-400" />
          <span className="font-overline text-[10px] text-emerald-400">
            tool · {m.tool_name}
          </span>
        </div>
        <pre className="text-xs font-mono text-zinc-300 p-3 whitespace-pre-wrap max-h-64 overflow-auto">
          {m.content}
        </pre>
      </div>
    );
  }
  if (m.role === "user") {
    return (
      <div className="flex justify-end my-4" data-testid="message-user">
        <div className="max-w-[75%] flex flex-col items-end gap-2">
          {m.images?.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-end">
              {m.images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="max-w-[180px] max-h-[180px] rounded-sm border border-zinc-700 object-cover"
                />
              ))}
            </div>
          )}
          {m.content && (
            <div className="bg-zinc-800 border border-zinc-700 rounded-sm px-4 py-2.5 text-sm text-white whitespace-pre-wrap">
              {m.content}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (!m.content && !m.tool_calls) return null;
  return (
    <div className="my-4 flex gap-3" data-testid="message-assistant">
      <img
        src={AVATAR}
        alt=""
        className="w-7 h-7 rounded-sm border border-zinc-700 mt-1 shrink-0 object-cover"
      />
      <div className="flex-1 border-l-2 border-white pl-4 min-w-0">
        {m.tool_calls && m.tool_calls.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {m.tool_calls.map((tc, i) => (
              <Badge
                key={i}
                variant="outline"
                className="rounded-sm border-emerald-700 text-emerald-300 bg-emerald-950/30 font-mono text-[10px]"
              >
                → {tc.function?.name}
              </Badge>
            ))}
          </div>
        )}
        {m.content && (
          <div className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">
            {m.content}
            {m._streaming && (
              <span className="inline-block w-2 h-3 ml-1 bg-zinc-400 animate-pulse" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationList({ items, current, onSelect, onNew, onDelete, onExport }) {
  return (
    <div className="w-72 border-r border-zinc-800 bg-zinc-950 flex flex-col shrink-0">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <span className="font-overline text-[10px] text-zinc-500">
          Conversations
        </span>
        <button
          data-testid="new-conversation-button"
          onClick={onNew}
          className="w-6 h-6 flex items-center justify-center rounded-sm hover:bg-zinc-800 text-zinc-400 hover:text-white"
          title="New conversation"
        >
          <PlusIcon size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 && (
          <div className="px-4 py-6 text-xs text-zinc-600 font-mono">
            No conversations yet.
          </div>
        )}
        {items.map((c) => (
          <div
            key={c.id}
            data-testid={`conversation-row-${c.id}`}
            className={`group px-4 py-3 border-b border-zinc-900 cursor-pointer transition-colors flex items-start gap-2 ${
              current === c.id ? "bg-zinc-900" : "hover:bg-zinc-900/60"
            }`}
            onClick={() => onSelect(c.id)}
          >
            <ChatCircleIcon
              size={14}
              className="text-zinc-500 mt-0.5 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-100 truncate">
                {c.title || "Untitled"}
              </div>
              <div className="text-[10px] font-mono text-zinc-600 mt-1 flex items-center gap-2">
                {c.source}
                <span className="text-zinc-700">·</span>
                <span>{new Date(c.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-white"
                >
                  <DotsThreeIcon size={16} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-zinc-800 rounded-sm">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onExport(c.id, "markdown");
                  }}
                  data-testid={`export-md-${c.id}`}
                >
                  <DownloadSimpleIcon size={14} className="mr-2" /> Export .md
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onExport(c.id, "json");
                  }}
                  data-testid={`export-json-${c.id}`}
                >
                  <DownloadSimpleIcon size={14} className="mr-2" /> Export .json
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(c.id);
                  }}
                  className="text-red-400"
                >
                  <TrashIcon size={14} className="mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>
    </div>
  );
}

function AgentSidebar({ settings, providers, collections, selectedCollections, setSelectedCollections, recentTools }) {
  const provider = useMemo(
    () => providers.find((p) => p.id === settings?.active_provider_id),
    [providers, settings],
  );
  return (
    <aside className="w-80 border-l border-zinc-800 bg-zinc-950 flex flex-col shrink-0 overflow-y-auto">
      <div className="px-4 py-3 border-b border-zinc-800">
        <span className="font-overline text-[10px] text-zinc-500">
          Active Model
        </span>
        {provider ? (
          <div className="mt-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-dot" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-100 truncate">{provider.name}</div>
              <div className="text-[10px] font-mono text-zinc-500 truncate">
                {settings?.active_model || "(no model)"}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-2 text-xs text-yellow-500 flex items-center gap-2">
            <WarningIcon size={14} />
            <span>Configure in Settings →</span>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-2">
          <span className="font-overline text-[10px] text-zinc-500">Sandbox</span>
          <Badge
            variant="outline"
            className={`rounded-sm font-mono text-[10px] border ${
              settings?.sandboxed_execution
                ? "border-emerald-700 text-emerald-300 bg-emerald-950/30"
                : "border-red-700 text-red-300 bg-red-950/30"
            }`}
          >
            {settings?.sandboxed_execution ? "ON" : "OFF · HOST ACCESS"}
          </Badge>
        </div>
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          {settings?.sandboxed_execution
            ? "Code & shell limited to /app/agent_workspace."
            : "Unsandboxed. Agent can read/write anywhere on the host."}
        </div>
      </div>

      <div className="px-4 py-3 border-b border-zinc-800">
        <span className="font-overline text-[10px] text-zinc-500">Tools Enabled</span>
        <div className="mt-2 flex flex-wrap gap-1">
          {Object.entries(settings?.tools_enabled || {}).map(([k, v]) => (
            <Badge
              key={k}
              variant="outline"
              className={`rounded-sm font-mono text-[10px] ${
                v
                  ? "border-zinc-700 text-zinc-200 bg-zinc-900"
                  : "border-zinc-900 text-zinc-600 bg-zinc-950"
              }`}
            >
              {v ? "✓" : "✗"} {k}
            </Badge>
          ))}
        </div>
      </div>

      <div className="px-4 py-3 border-b border-zinc-800">
        <span className="font-overline text-[10px] text-zinc-500">
          Knowledge Attachments
        </span>
        <div className="mt-2 space-y-1">
          {collections.length === 0 && (
            <div className="text-[11px] text-zinc-600 font-mono">No collections.</div>
          )}
          {collections.map((c) => {
            const on = selectedCollections.includes(c.id);
            return (
              <button
                key={c.id}
                data-testid={`toggle-collection-${c.id}`}
                onClick={() =>
                  setSelectedCollections((cur) =>
                    on ? cur.filter((x) => x !== c.id) : [...cur, c.id],
                  )
                }
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded-sm border text-xs ${
                  on
                    ? "border-yellow-700 bg-yellow-950/30 text-yellow-200"
                    : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
                }`}
              >
                <span className="truncate">{c.name}</span>
                <span className="font-mono text-[10px] text-zinc-500">
                  {c.doc_count}d / {c.chunk_count}c
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-3 flex-1 min-h-0">
        <span className="font-overline text-[10px] text-zinc-500">
          Recent Tool Executions
        </span>
        <div className="mt-2 space-y-1.5">
          {recentTools.length === 0 && (
            <div className="text-[11px] text-zinc-600 font-mono">No tool calls yet.</div>
          )}
          {recentTools.slice(0, 12).map((t) => (
            <div
              key={t.id}
              className="border border-zinc-800 bg-black rounded-sm px-2 py-1.5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 min-w-0">
                  <CaretRightIcon size={10} className="text-emerald-400" />
                  <span className="font-mono text-[11px] text-emerald-300 truncate">
                    {t.tool_name}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-zinc-600 shrink-0">
                  {t.duration_ms}ms
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Dashboard() {
  const [conversations, setConversations] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [toolExecs, setToolExecs] = useState([]);
  const [input, setInput] = useState("");
  const [images, setImages] = useState([]); // data URIs
  const [sending, setSending] = useState(false);
  const [providers, setProviders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    if (currentId) loadMessages(currentId);
    else {
      setMessages([]);
      setToolExecs([]);
    }
  }, [currentId]);

  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const refreshAll = async () => {
    try {
      const [cv, pr, st, co] = await Promise.all([
        api.get("/conversations"),
        api.get("/providers"),
        api.get("/settings"),
        api.get("/collections"),
      ]);
      setConversations(cv.data);
      setProviders(pr.data);
      setSettings(st.data);
      setCollections(co.data);
      if (cv.data.length && !currentId) setCurrentId(cv.data[0].id);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMessages = async (id) => {
    const [m, t] = await Promise.all([
      api.get(`/conversations/${id}/messages`),
      api.get(`/conversations/${id}/tool-executions`),
    ]);
    setMessages(m.data);
    setToolExecs(t.data);
  };

  const newConversation = async () => {
    const { data } = await api.post("/conversations");
    setConversations((c) => [data, ...c]);
    setCurrentId(data.id);
    setMessages([]);
    setToolExecs([]);
  };

  const deleteConversation = async (id) => {
    if (!confirm("Delete conversation?")) return;
    await api.delete(`/conversations/${id}`);
    setConversations((c) => c.filter((x) => x.id !== id));
    if (currentId === id) {
      setCurrentId(null);
      setMessages([]);
    }
  };

  const exportConversation = async (id, format) => {
    try {
      const res = await api.get(`/conversations/${id}/export`, {
        params: { format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `conversation-${id}.${format === "json" ? "json" : "md"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const addImages = async (files) => {
    const list = Array.from(files || []).slice(0, 4);
    const urls = await Promise.all(list.map(fileToDataURL));
    setImages((cur) => [...cur, ...urls].slice(0, 6));
  };

  const send = async () => {
    if ((!input.trim() && images.length === 0) || sending) return;
    if (!settings?.active_provider_id || !settings?.active_model) {
      toast.error("Pick a provider & model in Settings first.");
      return;
    }
    const userContent = input;
    const userImages = images;
    setInput("");
    setImages([]);
    setSending(true);

    // Append optimistic user message
    const tempUserId = `tmp-user-${Date.now()}`;
    setMessages((m) => [
      ...m,
      {
        id: tempUserId,
        role: "user",
        content: userContent,
        images: userImages,
        created_at: new Date().toISOString(),
      },
    ]);

    let convId = currentId;
    let streamingAssistantId = null;

    try {
      await streamChat(
        {
          conversation_id: currentId,
          content: userContent,
          images: userImages,
          collection_ids: selectedCollections,
        },
        (ev) => {
          if (ev.type === "start") {
            convId = ev.conversation_id;
            if (!currentId) setCurrentId(convId);
            // Replace temp user id with real id
            setMessages((m) =>
              m.map((x) => (x.id === tempUserId ? ev.user_message : x)),
            );
            // Create a streaming assistant placeholder
            streamingAssistantId = `tmp-assistant-${Date.now()}`;
            setMessages((m) => [
              ...m,
              {
                id: streamingAssistantId,
                role: "assistant",
                content: "",
                created_at: new Date().toISOString(),
                _streaming: true,
              },
            ]);
          } else if (ev.type === "delta") {
            setMessages((m) =>
              m.map((x) =>
                x.id === streamingAssistantId
                  ? { ...x, content: (x.content || "") + ev.content }
                  : x,
              ),
            );
          } else if (ev.type === "assistant_done") {
            // Replace placeholder with persisted assistant message
            setMessages((m) =>
              m.map((x) =>
                x.id === streamingAssistantId ? ev.message : x,
              ),
            );
            // Next iteration may stream another assistant message
            streamingAssistantId = `tmp-assistant-${Date.now()}`;
            // Pre-create placeholder for next iteration
            setMessages((m) => [
              ...m,
              {
                id: streamingAssistantId,
                role: "assistant",
                content: "",
                created_at: new Date().toISOString(),
                _streaming: true,
              },
            ]);
          } else if (ev.type === "tool_result") {
            // Remove the unused empty assistant placeholder, then add tool
            setMessages((m) => {
              const filtered = m.filter(
                (x) =>
                  !(
                    x.id === streamingAssistantId &&
                    !x.content &&
                    !x.tool_calls
                  ),
              );
              return [...filtered, ev.message];
            });
            setToolExecs((t) => [ev.execution, ...t]);
            // Recreate placeholder for next assistant turn
            streamingAssistantId = `tmp-assistant-${Date.now()}`;
            setMessages((m) => [
              ...m,
              {
                id: streamingAssistantId,
                role: "assistant",
                content: "",
                created_at: new Date().toISOString(),
                _streaming: true,
              },
            ]);
          } else if (ev.type === "done") {
            // Remove any empty trailing placeholder
            setMessages((m) =>
              m.filter(
                (x) =>
                  !(
                    typeof x.id === "string" &&
                    x.id.startsWith("tmp-assistant-") &&
                    !x.content &&
                    !x.tool_calls
                  ),
              ),
            );
          } else if (ev.type === "error") {
            toast.error(ev.error);
          }
        },
      );
      // Refresh conversation list to update title/order
      const cv = await api.get("/conversations");
      setConversations(cv.data);
    } catch (e) {
      toast.error(e?.message || "Send failed");
      // Roll back the optimistic user message on hard fail
      setMessages((m) => m.filter((x) => x.id !== tempUserId));
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="h-full flex" data-testid="dashboard">
      <ConversationList
        items={conversations}
        current={currentId}
        onSelect={setCurrentId}
        onNew={newConversation}
        onDelete={deleteConversation}
        onExport={exportConversation}
      />

      <section className="flex-1 flex flex-col min-w-0">
        <header className="px-6 py-3 border-b border-zinc-800 flex items-center justify-between bg-zinc-950">
          <div>
            <div className="font-overline text-[10px] text-zinc-500">
              DoppelGanger // Command Center
            </div>
            <div className="font-heading text-base tracking-tight">
              {conversations.find((c) => c.id === currentId)?.title ||
                "New Conversation"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {settings?.active_provider_id ? (
              <span className="flex items-center gap-1.5 text-xs text-emerald-400 font-mono">
                <CheckCircleIcon size={14} weight="fill" />
                online · streaming
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs text-yellow-500 font-mono">
                <WarningIcon size={14} /> no model
              </span>
            )}
          </div>
        </header>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 py-6 bg-zinc-950"
          data-testid="messages-container"
        >
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto">
              <img
                src={AVATAR}
                alt=""
                className="w-16 h-16 rounded-sm border border-zinc-800 mb-4"
              />
              <div className="font-overline text-[10px] text-zinc-500 mb-2">
                Waiting for instruction
              </div>
              <h2 className="font-heading text-2xl tracking-tighter mb-2">
                Tell me what you need done.
              </h2>
              <p className="text-sm text-zinc-500 leading-relaxed">
                Attach knowledge collections or images. Toggle the sandbox in
                Settings. Code, search, files, shell — all yours, streamed live.
              </p>
            </div>
          )}
          {messages.map((m) => (
            <MessageItem key={m.id} m={m} />
          ))}
        </div>

        <footer className="border-t border-zinc-800 bg-zinc-950 px-6 py-4">
          {images.length > 0 && (
            <div className="mb-2 flex gap-2 flex-wrap" data-testid="image-attachments">
              {images.map((src, i) => (
                <div
                  key={i}
                  className="relative w-16 h-16 border border-zinc-700 rounded-sm overflow-hidden"
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() =>
                      setImages((c) => c.filter((_, idx) => idx !== i))
                    }
                    className="absolute top-0 right-0 bg-black/80 text-white w-5 h-5 flex items-center justify-center"
                  >
                    <XIcon size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                addImages(e.target.files);
                e.target.value = "";
              }}
            />
            <Button
              data-testid="attach-image-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 h-[60px] w-[60px] p-0"
              title="Attach image"
            >
              <ImageIcon size={18} className="text-zinc-300" />
            </Button>
            <Textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Instruct your DoppelGanger..."
              className="resize-none min-h-[60px] max-h-40 rounded-sm bg-black border-zinc-800 focus-visible:ring-1 focus-visible:ring-zinc-500 font-mono text-sm"
              disabled={sending}
            />
            <Button
              data-testid="chat-send-button"
              onClick={send}
              disabled={sending || (!input.trim() && images.length === 0)}
              className="bg-white text-black hover:bg-zinc-200 rounded-sm h-[60px] px-5"
            >
              <PaperPlaneTiltIcon size={18} weight="fill" />
            </Button>
          </div>
          {selectedCollections.length > 0 && (
            <div className="mt-2 flex items-center gap-2 text-[10px] font-mono text-yellow-500">
              <span>RAG ATTACHED:</span>
              {selectedCollections.map((cid) => {
                const c = collections.find((x) => x.id === cid);
                return c ? (
                  <Badge
                    key={cid}
                    variant="outline"
                    className="rounded-sm border-yellow-800 bg-yellow-950/30 text-yellow-200"
                  >
                    {c.name}
                  </Badge>
                ) : null;
              })}
            </div>
          )}
        </footer>
      </section>

      <AgentSidebar
        settings={settings}
        providers={providers}
        collections={collections}
        selectedCollections={selectedCollections}
        setSelectedCollections={setSelectedCollections}
        recentTools={toolExecs}
      />
    </div>
  );
}
